import * as React from 'react'
import { Link, useSearchParams } from 'react-router'
import { ChevronLeft, ChevronRight, FileText, Search } from 'lucide-react'
import type { EnvelopeListItemDto } from '@lib/dto/envelope.dto'
import { ENVELOPE_STATUSES, envelopeStatusSchema, type EnvelopeStatus } from '@lib/enums/envelope-status.enum'
import type { ProductSurface } from '@lib/enums/product-surface.enum'
import { tenantModeSchema, type TenantMode } from '@lib/enums/tenant-mode.enum'
import {
  envelopeSearchQuerySchema,
  opsEnvelopeSearchQuerySchema,
  type EnvelopeSearchQuery,
  type OpsEnvelopeSearchQuery,
} from '@lib/input/envelope.input'
import { emailSchema } from '@lib/validators/email.validator'
import { EnvelopeStatusBadge } from '@/components/envelopes/StatusBadge'
import { PageHeader } from '@/components/PageHeader'
import { GoLiveChecklist } from '@/dashboard/GoLiveChecklist'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useEnvelopeAccess } from '@/envelopes/EnvelopeAccess'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useEnvelopes } from '@/hooks/useEnvelopes'
import { useOpsTenants } from '@/hooks/useOpsTenants'
import { uuidSchema, type Uuid } from '@lib/validators/params'

const PAGE_SIZE = 25

/** The text filters as typed (applied to the URL on submit). */
interface FilterDraft {
  q: string
  email: string
  metadataKey: string
  metadataValue: string
}

function statusLabel(status: EnvelopeStatus): string {
  return status.charAt(0).toUpperCase() + status.slice(1)
}

/** The one rule across parameters: a metadata key and value come together. Breaking it drops the pair. */
const METADATA_PAIR = ['metadataKey', 'metadataValue']

/**
 * The search in the URL, validated for this surface. A bad or unknown
 * parameter (a hand-edited or stale link) is dropped on its own, so the rest
 * of the search still applies. Dashboard links never keep staff-only
 * `tenantId` / `mode`; those belong to the ops schema.
 */
export function searchFromParams(params: URLSearchParams, surface: 'dashboard'): EnvelopeSearchQuery
export function searchFromParams(params: URLSearchParams, surface: 'ops'): OpsEnvelopeSearchQuery
export function searchFromParams(
  params: URLSearchParams,
  surface: ProductSurface,
): EnvelopeSearchQuery | OpsEnvelopeSearchQuery {
  const schema = surface === 'ops' ? opsEnvelopeSearchQuerySchema : envelopeSearchQuerySchema
  const raw: Record<string, string> = Object.fromEntries(params.entries())
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const parsed = schema.safeParse(raw)
    if (parsed.success) return parsed.data
    for (const issue of parsed.error.issues) {
      let keys: readonly PropertyKey[] = [issue.path[0] ?? '']
      if (issue.code === 'unrecognized_keys') keys = issue.keys
      else if (issue.code === 'custom' && issue.path[0] === 'metadataKey') keys = METADATA_PAIR
      for (const key of keys) {
        if (typeof key === 'string') delete raw[key]
      }
    }
  }
  return {}
}

/** Which surface the validated search belongs to, so ops-only fields stay on the ops variant. */
type ListedSearch =
  | { surface: 'dashboard'; query: EnvelopeSearchQuery }
  | { surface: 'ops'; query: OpsEnvelopeSearchQuery }

function draftFrom(search: EnvelopeSearchQuery): FilterDraft {
  return {
    q: search.q ?? '',
    email: search.email ?? '',
    metadataKey: search.metadataKey ?? '',
    metadataValue: search.metadataValue ?? '',
  }
}

function signerSummary(envelope: EnvelopeListItemDto): string {
  const [first, ...rest] = envelope.signers
  if (!first) return '—'
  return rest.length > 0 ? `${first.name} +${String(rest.length)}` : first.name
}

interface OpsFiltersProps {
  tenantId: string
  mode: TenantMode
  onTenantId: (tenantId: Uuid | undefined) => void
  onMode: (mode: TenantMode) => void
}

/** Staff-only: which tenant, and live or test (live unless asked). */
/** The chosen tenant's id, or undefined for "All tenants". */
function tenantFrom(value: string): Uuid | undefined {
  const parsed = uuidSchema.safeParse(value)
  return parsed.success ? parsed.data : undefined
}

function OpsFilters({ tenantId, mode, onTenantId, onMode }: OpsFiltersProps) {
  const tenants = useOpsTenants()
  return (
    <>
      <Field label="Tenant" htmlFor="filter-tenant" className="w-44">
        <Select id="filter-tenant" value={tenantId} onChange={(event) => onTenantId(tenantFrom(event.target.value))}>
          <option value="">All tenants</option>
          {(tenants.data?.tenants ?? []).map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Mode" htmlFor="filter-mode" className="w-28">
        <Select
          id="filter-mode"
          value={mode}
          onChange={(event) => {
            const parsed = tenantModeSchema.safeParse(event.target.value)
            if (parsed.success) onMode(parsed.data)
          }}
        >
          <option value="live">Live</option>
          <option value="test">Test</option>
        </Select>
      </Field>
    </>
  )
}

/**
 * Envelopes, newest first, with search: status, title, signer email, and one
 * metadata entry (plus tenant and mode for staff). The search lives in the URL,
 * so a filtered list can be shared or bookmarked.
 */
export function EnvelopesPage() {
  const access = useEnvelopeAccess()
  const [params, setParams] = useSearchParams()
  const listed = React.useMemo((): ListedSearch => {
    if (access.surface === 'ops') {
      return { surface: 'ops', query: searchFromParams(params, 'ops') }
    }
    return { surface: 'dashboard', query: searchFromParams(params, 'dashboard') }
  }, [params, access.surface])
  const search = listed.query
  const offset = search.offset ?? 0
  const query = useEnvelopes({ ...search, limit: PAGE_SIZE, offset })
  const [draft, setDraft] = React.useState<FilterDraft>(() => draftFrom(search))
  const [emailError, setEmailError] = React.useState<string | null>(null)
  const opsFilters = listed.surface === 'ops' ? [listed.query.tenantId, listed.query.mode] : []
  const filtered = [search.status, search.q, search.email, search.metadataKey, ...opsFilters].some(
    (value) => value !== undefined,
  )

  // Back/forward and shared links change the URL without touching the inputs,
  // so reset them when the URL's search changes (during render, not in an
  // effect). Compared as text: the same search parsed again isn't a change, and
  // mustn't throw away what's typed but not yet applied.
  const searchText = params.toString()
  const [draftSearchText, setDraftSearchText] = React.useState(searchText)
  if (draftSearchText !== searchText) {
    setDraftSearchText(searchText)
    setDraft(draftFrom(search))
  }

  /** Sets the given parameters (`undefined` removes one) and goes back to the first page. */
  function update(changes: { [K in keyof OpsEnvelopeSearchQuery]?: OpsEnvelopeSearchQuery[K] | undefined }): void {
    const next = new URLSearchParams(params)
    for (const [key, value] of Object.entries(changes)) {
      if (value === undefined) next.delete(key)
      else next.set(key, String(value))
    }
    next.delete('offset')
    setParams(next)
  }

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const email = draft.email.trim()
    if (email && !emailSchema.safeParse(email).success) {
      setEmailError('Enter a full email address')
      return
    }
    setEmailError(null)
    const key = draft.metadataKey.trim()
    update({
      q: draft.q.trim() || undefined,
      email: email || undefined,
      // An empty value is a real filter ("loanId is blank"), so it's kept whenever there's a key.
      metadataKey: key || undefined,
      metadataValue: key ? draft.metadataValue.trim() : undefined,
    })
  }

  function goTo(nextOffset: number): void {
    const next = new URLSearchParams(params)
    if (nextOffset > 0) next.set('offset', String(nextOffset))
    else next.delete('offset')
    setParams(next)
  }

  function renderTable(envelopes: EnvelopeListItemDto[]) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Status</TableHead>
            {access.surface === 'ops' ? <TableHead>Mode</TableHead> : null}
            <TableHead>Signers</TableHead>
            <TableHead>Created</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {envelopes.map((envelope) => (
            <TableRow key={envelope.id}>
              <TableCell>
                <Link to={access.detailPath(envelope.id)} className="font-medium text-link hover:underline">
                  {envelope.title}
                </Link>
              </TableCell>
              <TableCell>
                <EnvelopeStatusBadge status={envelope.status} />
              </TableCell>
              {access.surface === 'ops' ? (
                <TableCell>
                  <Badge tone={envelope.mode === 'test' ? 'warning' : 'neutral'}>{envelope.mode}</Badge>
                </TableCell>
              ) : null}
              <TableCell className="whitespace-nowrap">{signerSummary(envelope)}</TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {new Date(envelope.createdAt).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  function renderPager(total: number) {
    if (total <= PAGE_SIZE) return null
    const last = Math.min(offset + PAGE_SIZE, total)
    return (
      <div className="mt-3 flex items-center justify-end gap-2 text-body text-muted-foreground">
        <span>
          {String(offset + 1)}–{String(last)} of {String(total)}
        </span>
        <Button variant="secondary" size="icon-sm" aria-label="Previous page" disabled={offset === 0} onClick={() => goTo(Math.max(0, offset - PAGE_SIZE))}>
          <ChevronLeft />
        </Button>
        <Button variant="secondary" size="icon-sm" aria-label="Next page" disabled={last >= total} onClick={() => goTo(offset + PAGE_SIZE)}>
          <ChevronRight />
        </Button>
      </div>
    )
  }

  function renderBody() {
    if (query.isLoading) return <LoadingState />
    if (query.isError || !query.data) {
      return <ErrorState message={apiErrorMessage(query.error, 'Could not load envelopes.')} />
    }
    if (query.data.envelopes.length === 0) {
      return filtered ? (
        <EmptyState
          icon={Search}
          title="No envelopes match"
          description="Try fewer filters."
          action={
            <Button variant="secondary" size="sm" onClick={() => setParams(new URLSearchParams())}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <EmptyState icon={FileText} title="No envelopes yet" description="Envelopes created with your API keys appear here." />
      )
    }
    return (
      <>
        {renderTable(query.data.envelopes)}
        {renderPager(query.data.total)}
      </>
    )
  }

  return (
    <>
      <PageHeader title="Envelopes" description={query.data ? `${String(query.data.total)} total` : undefined} />
      {access.surface === 'dashboard' ? <GoLiveChecklist /> : null}
      {access.notice}
      <form onSubmit={handleSubmit} className="mb-4 flex flex-wrap items-end gap-3" noValidate>
        <Field label="Status" htmlFor="filter-status" className="w-36">
          <Select
            id="filter-status"
            value={search.status ?? ''}
            onChange={(event) => {
              const status = envelopeStatusSchema.safeParse(event.target.value)
              update({ status: status.success ? status.data : undefined })
            }}
          >
            <option value="">Any status</option>
            {ENVELOPE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {statusLabel(status)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title" htmlFor="filter-q" className="min-w-40 flex-1">
          <Input id="filter-q" placeholder="Contains…" value={draft.q} onChange={(event) => setDraft({ ...draft, q: event.target.value })} />
        </Field>
        <Field label="Signer email" htmlFor="filter-email" className="w-52" error={emailError}>
          <Input
            id="filter-email"
            type="email"
            placeholder="borrower@example.com"
            value={draft.email}
            aria-invalid={emailError ? true : undefined}
            aria-describedby={fieldMessageId('filter-email')}
            onChange={(event) => setDraft({ ...draft, email: event.target.value })}
          />
        </Field>
        <Field label="Metadata" htmlFor="filter-metadata-key" className="w-32">
          <Input
            id="filter-metadata-key"
            placeholder="key"
            className="font-mono"
            value={draft.metadataKey}
            onChange={(event) => setDraft({ ...draft, metadataKey: event.target.value })}
          />
        </Field>
        <Field label="Value" htmlFor="filter-metadata-value" className="w-32">
          <Input
            id="filter-metadata-value"
            placeholder="value"
            className="font-mono"
            value={draft.metadataValue}
            disabled={draft.metadataKey.trim().length === 0}
            onChange={(event) => setDraft({ ...draft, metadataValue: event.target.value })}
          />
        </Field>
        {listed.surface === 'ops' ? (
          <OpsFilters
            tenantId={listed.query.tenantId ?? ''}
            mode={listed.query.mode ?? 'live'}
            onTenantId={(tenantId) => update({ tenantId })}
            onMode={(mode) => update({ mode: mode === 'test' ? 'test' : undefined })}
          />
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" variant="secondary">
            <Search />
            Search
          </Button>
          {filtered ? (
            <Button type="button" variant="ghost" onClick={() => setParams(new URLSearchParams())}>
              Clear
            </Button>
          ) : null}
        </div>
      </form>
      {renderBody()}
    </>
  )
}
